import { ExtractComponentProp } from '@approvalmax/types';
import { DeviceNotebookIcon } from '@approvalmax/ui';
import {
    Box,
    Button,
    Grid,
    Link,
    List,
    Popup,
    Progress,
    Stepper,
    Text,
    useConfirmation,
} from '@approvalmax/ui/src/components';
import encodeQR from '@paulmillr/qr';
import { useAtom, useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import { useMountedState } from 'react-use';
import { ConsentStatus, useGetAmaxPayConsent, useGetAmaxPayOpenBankingBanks } from 'shared/data/webApp/v1';

import { useMatchXeroPopupQueryParams } from '../../hooks/useMatchXeroPopupQueryParams';
import { connectionCanceledPopupOpenState } from '../ConnectionCanceledPopup/ConnectionCanceledPopup.states';
import { messages } from './BankingAppLoginPopup.messages';
import { bankingAppLoginPopupState } from './BankingAppLoginPopup.states';

export const BankingAppLoginPopup = memo(() => {
    const { companyId } = useParams<{ companyId: string }>();
    const [popupState, setPopupState] = useAtom(bankingAppLoginPopupState);
    const setConnectionCanceledPopupOpen = useSetAtom(connectionCanceledPopupOpenState);
    const { setConsentId, setBankAccountId } = useMatchXeroPopupQueryParams();
    const isMounted = useMountedState();
    const [activeStep, setActiveStep] = useState(0);
    const { confirmation } = useConfirmation({
        confirmMessage: messages.areYouSure,
        confirmButtonMessage: messages.yesStop,
    });

    const { data: amaxPayOpenBankingBanks, isLoading: isLoadingGetAmaxPayOpenBankingBanks } =
        useGetAmaxPayOpenBankingBanks({ path: { companyId } }, { enabled: typeof popupState.consentId === 'string' });

    const { data: amaxPayConsent } = useGetAmaxPayConsent(
        { path: { companyId, consentId: popupState.consentId } },
        { enabled: typeof popupState.consentId === 'string', refetchInterval: 5 * 1000 }
    );

    const bank = useMemo(
        () => amaxPayOpenBankingBanks?.items.find((bank) => bank.id === popupState.bankId),
        [amaxPayOpenBankingBanks?.items, popupState.bankId]
    );

    const qrCodeSrc = useMemo(
        () => `data:image/svg+xml;utf8,${encodeURIComponent(encodeQR(popupState.authorisationUrl ?? '', 'svg'))}`,
        [popupState.authorisationUrl]
    );

    const closePopup = useCallback(() => {
        setPopupState((prevState) => ({ ...prevState, consentId: undefined }));
        setActiveStep(0);
    }, [setPopupState]);

    // This is for mobile flow
    useEffect(() => {
        let timerId: ReturnType<typeof setTimeout>;

        switch (amaxPayConsent?.data.status) {
            case ConsentStatus.Authorized:
                setActiveStep(2);
                // setTimeout here is just to make UX more friendly, otherwise
                // user never see loading state on the 3rd stepper's step
                timerId = setTimeout(() => {
                    setConsentId(amaxPayConsent.data.id);
                    closePopup();
                }, 2 * 1000);
                break;

            // when cancel connection on mobile, immediately show failure popup
            case ConsentStatus.Rejected:
                setConnectionCanceledPopupOpen(true);
                closePopup();
                break;

            default: {
                if (amaxPayConsent?.data.authorisationUrlOpenedAt) {
                    setActiveStep((prevActiveStep) => {
                        if (prevActiveStep === 0) {
                            return 1;
                        }

                        return prevActiveStep;
                    });
                }
            }
        }

        return () => {
            timerId && clearTimeout(timerId);
        };
    }, [
        amaxPayConsent?.data.authorisationUrlOpenedAt,
        amaxPayConsent?.data.id,
        amaxPayConsent?.data.status,
        setConsentId,
        setBankAccountId,
        setPopupState,
        setConnectionCanceledPopupOpen,
        closePopup,
    ]);

    // close popup and show failure popup after 15 mins of inactivity
    useEffect(() => {
        let timerId: ReturnType<typeof setTimeout>;

        if (typeof popupState.consentId === 'string') {
            timerId = setTimeout(
                () => {
                    if (isMounted()) {
                        setConnectionCanceledPopupOpen(true);
                        closePopup();
                    }
                },
                15 * 60 * 1000
            );
        }

        return () => {
            clearTimeout(timerId);
        };
    }, [closePopup, isMounted, popupState.consentId, setConnectionCanceledPopupOpen]);

    const handleTogglePopup: ExtractComponentProp<typeof Popup, 'onToggle'> = (open) => {
        if (!open) {
            void confirmation()
                .then(closePopup)
                .catch(() => {});
        }
    };

    return (
        <Popup
            className='fs-exclude'
            size='small'
            open={typeof popupState.consentId === 'string'}
            onToggle={handleTogglePopup}
        >
            <Popup.Header
                title={messages.connectSecurelyWith({ bank: bank?.name ?? <Skeleton width={100} /> })}
                progress={isLoadingGetAmaxPayOpenBankingBanks}
            />

            <Popup.Body spacing='32'>
                <Grid gap={24}>
                    <Grid gap={12} justifyContent='center'>
                        <Box spacing='16 52' color='midnight20' radius='xsmall'>
                            <Grid gap={40} gridTemplateColumns='auto auto' alignItems='center'>
                                <img width={160} height={160} src={qrCodeSrc} alt='mobile application qr-code' />

                                <Grid gap={12} justifyItems='center'>
                                    <Text uppercase font='label' fontSize='small' color='midnight70'>
                                        {messages.or}
                                    </Text>

                                    <Button
                                        as='a'
                                        color='blue80'
                                        size='medium'
                                        rel='noopener noreferrer'
                                        href={popupState.authorisationUrl}
                                        startIcon={<DeviceNotebookIcon color='white100' />}
                                    >
                                        {messages.loginOnDesktop}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    <Stepper direction='vertical' activeStep={activeStep}>
                        <Stepper.Step label={messages.securelyLogin} />

                        <Stepper.Step
                            label={messages.authoriseTheConnection}
                            icon={activeStep === 1 ? <Progress shape='circle' size='xsmall' /> : undefined}
                        />

                        <Stepper.Step
                            label={messages.returnTo}
                            icon={activeStep === 2 ? <Progress shape='circle' size='xsmall' /> : undefined}
                        />
                    </Stepper>

                    <Text font='body' fontSize='small' color='midnight70'>
                        {messages.byUsingYouAgree({
                            termsAndConditions: (
                                <Link
                                    font='body'
                                    fontSize='small'
                                    external
                                    href='https://www.yapily.com/legal/end-user-terms'
                                >
                                    {messages.termsAndConditions}
                                </Link>
                            ),
                            privacyNotice: (
                                <Link
                                    font='body'
                                    fontSize='small'
                                    external
                                    href='https://www.yapily.com/legal/privacy-policy'
                                >
                                    {messages.privacyNotice}
                                </Link>
                            ),
                        })}
                    </Text>

                    <Text font='body' fontSize='small' color='midnight70'>
                        {messages.youllBeRedirected({ bankName: bank?.name ?? <Skeleton width={60} /> })}

                        <List listStyle='inside'>
                            <List.Item>{messages.accountDetails}</List.Item>

                            <List.Item>{messages.balances}</List.Item>

                            <List.Item>{messages.transactionHistory}</List.Item>
                        </List>
                    </Text>

                    <Text font='body' fontSize='small' color='midnight70'>
                        {messages.weHavePartnered({ bankName: bank?.name ?? <Skeleton width={60} /> })}
                    </Text>
                </Grid>
            </Popup.Body>
        </Popup>
    );
});
